/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { TbHelp, TbExternalLink, TbPhone, TbMail, TbMapPin } from 'react-icons/tb'

import { PageHeader, PageMenu, MenuAction, PageContainer } from '@/components/ui'
import { useAuth } from '@/hooks'

import AdditionalResources from './AdditionalResources'

const HelpPage: React.FC = () => {
  const title = 'Help'
  const { hasPermission } = useAuth()
  const canSeeExtras = hasPermission('staff')

  // Quick Help Actions as menu actions
  const menuActions: MenuAction[] = [
    {
      id: 'us-help-center',
      label: 'US Help Center',
      icon: <TbExternalLink />,
      onClick: () => window.open('https://supportus.cooltrax.com', '_blank', 'noopener,noreferrer'),
      variant: 'primary',
      tooltip: 'Get support for Americas region',
    },
    {
      id: 'apac-help-center',
      label: 'APAC Help Center',
      icon: <TbExternalLink />,
      onClick: () => window.open('https://support.cooltrax.com', '_blank', 'noopener,noreferrer'),
      variant: 'secondary',
      tooltip: 'Get support for Asia Pacific region',
    },
  ]

  return (
    <div className="h-full overflow-y-auto">
      <PageContainer>
        <div className="space-y-8">
          <PageHeader
            title={title}
            subtitle="Get help and support for Cooltrax Fresh - Access help centers and contact our support team directly"
            icon={<TbHelp />}
            menu={<PageMenu actions={menuActions} />}
          />

          {/* Regional Support Centers */}
          <div className="card card-border bg-base-100">
            <div className="card-body">
              <h2 className="card-title text-xl mb-6 text-primary">
                <TbPhone className="w-6 h-6" />
                Regional Support Centers
              </h2>
              <p className="text-base-content/80 mb-8">
                Connect with our regional support teams for personalized assistance and expert guidance. Access help
                centers and contact support directly.
              </p>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Asia Pacific Support Center */}
                <div className="card card-border bg-gradient-to-br from-secondary/5 to-accent/5 hover:shadow-md transition-shadow">
                  <div className="card-body">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="avatar">
                        <div className="w-12 rounded-full">
                          <span className="text-2xl">🇦🇺</span>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-secondary">Asia Pacific</h3>
                        <div className="badge badge-secondary badge-outline badge-xs">Primary Support</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbMapPin className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Office Address</div>
                            <div className="text-base-content/70 space-y-1">
                              <div>6/42 Sabre Drive, Port Melbourne</div>
                              <div>Victoria 3207, Australia</div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbPhone className="w-5 h-5 text-secondary flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Phone Support</div>
                            <a href="tel:+61396866011" className="link link-secondary text-base-content/70">
                              +61 3 9686 6011
                            </a>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbMail className="w-5 h-5 text-secondary flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Email Support</div>
                            <a href="mailto:support@cooltrax.com" className="link link-secondary text-base-content/70">
                              support@cooltrax.com
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="card-actions justify-between pt-4 border-t border-base-300">
                        <div className="alert alert-info alert-soft flex-1 mr-3">
                          <TbHelp className="w-5 h-5" />
                          <div className="flex-1">
                            <a href="#" className="link link-info font-medium" onClick={(e) => e.preventDefault()}>
                              Need help now?
                            </a>
                          </div>
                        </div>
                        <a
                          href="https://support.cooltrax.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm gap-2"
                        >
                          <TbExternalLink className="w-4 h-4" />
                          Help Center
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* North America Support Center */}
                <div className="card card-border bg-gradient-to-br from-primary/5 to-accent/5 hover:shadow-md transition-shadow">
                  <div className="card-body">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="avatar">
                        <div className="w-12 rounded-full">
                          <span className="text-2xl">🇺🇸</span>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-primary">North America/Canada</h3>
                        <div className="badge badge-primary badge-outline badge-xs">Regional Support</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbMapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Office Address</div>
                            <div className="text-base-content/70 space-y-1">
                              <div>11660 Alpharetta Highway</div>
                              <div>Suite 155</div>
                              <div>Roswell, GA 30076, USA</div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbPhone className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Phone Support</div>
                            <a href="tel:8776676868" className="link link-primary text-base-content/70">
                              877-667-6868
                            </a>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-lg bg-base-200/50">
                          <TbMail className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-base-content">Email Support</div>
                            <a href="mailto:support.us@cooltrax.com" className="link link-primary text-base-content/70">
                              support.us@cooltrax.com
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="card-actions justify-between pt-4 border-t border-base-300">
                        <div className="alert alert-warning alert-soft flex-1 mr-3">
                          <TbHelp className="w-5 h-5" />
                          <div className="flex-1">
                            <a href="#" className="link link-warning font-medium" onClick={(e) => e.preventDefault()}>
                              Need help now?
                            </a>
                          </div>
                        </div>
                        <a
                          href="https://supportus.cooltrax.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm gap-2"
                        >
                          <TbExternalLink className="w-4 h-4" />
                          Help Center
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Resources - staff only */}
          {canSeeExtras && <AdditionalResources />}
        </div>
      </PageContainer>
    </div>
  )
}

export default HelpPage
